import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { ResponseContext } from "../../../@api";

import { TokenRefreshManager } from "../../../auth/TokenRefreshManager";
import { IMiddelware } from "../../models/interfaces/middelware.interface";
import { UserStore } from "../../stores";

const RETRY_MARKER_HEADER = "x-token-refresh-retry";
const RETRY_MARKER_VALUE = "1";

/**
 * TokenRefreshMiddleware handles HTTP 403 responses by attempting to refresh the access token.
 *
 * Behavior (Silent-First Strategy):
 * 1. Catches HTTP 403 responses from the backend.
 * 2. Determines if the 403 is session-expired (not permission-related).
 * 3. Attempts silent token refresh using MSAL (no user interaction).
 * 4. If silent refresh succeeds, updates token and retries the original request transparently.
 * 5. If silent refresh fails with InteractionRequiredAuthError, falls back to popup reauthentication.
 * 6. If popup fails, falls back to redirect-based token acquisition.
 * 7. If all token acquisition attempts fail, returns the original 403 response path.
 */
export class TokenRefreshMiddleware implements IMiddelware {
  private static popupReauthenticationPromise: Promise<string | null> | null = null;

  public readonly unhandledHttpStatusCodes = [403];

  constructor(private readonly userStore: UserStore) {}

  public setError(context: ResponseContext): void {
    void context;
  }

  public async post(context: ResponseContext): Promise<Response | void> {
    if (context.response.status === 403 && !this.hasRetryMarker(context)) {
      return await this.handleTokenExpiry(context);
    }
  }

  public resetError() {}

  private async handleTokenExpiry(context: ResponseContext): Promise<Response | void> {
    try {
      const isSessionExpired = await this.isSessionExpired403(context);
      if (!isSessionExpired) {
        return;
      }

      const tokenRefreshManager = TokenRefreshManager.getInstance();

      // Step 1: Attempt silent token refresh first (SILENT-FIRST strategy).
      let newAccessToken: string | null = null;
      try {
        newAccessToken = await tokenRefreshManager.refreshAccessToken();
        if (newAccessToken) {
          console.debug("TokenRefreshMiddleware: Access token refreshed silently");
        }
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          console.debug(
            "TokenRefreshMiddleware: Silent token refresh requires interaction, attempting popup reauthentication",
          );
        } else {
          throw error;
        }
      }

      // Step 2: If silent refresh failed, use popup as fallback.
      if (!newAccessToken) {
        newAccessToken = await this.acquireTokenWithPopupQueue(tokenRefreshManager);
      }

      // Step 3: If popup also failed, use redirect as final fallback.
      if (!newAccessToken) {
        console.warn("TokenRefreshMiddleware: Could not acquire new access token via popup reauthentication");
        await tokenRefreshManager.acquireTokenRedirectFallback();
        return;
      }

      this.userStore.setAccessToken(newAccessToken);
      console.debug("TokenRefreshMiddleware: Access token updated in store");

      return await this.retryRequest(context, newAccessToken);
    } catch (error) {
      console.error("TokenRefreshMiddleware: Unexpected error during token refresh", error);
    }
  }

  private async isSessionExpired403(context: ResponseContext): Promise<boolean> {
    const authHeader = context.response.headers.get("www-authenticate")?.toLowerCase() ?? "";
    if (authHeader.includes("invalid_token") || authHeader.includes("expired")) {
      return true;
    }

    const responseClone = context.response.clone();
    let payloadText = "";

    try {
      const body = (await responseClone.json()) as Record<string, unknown>;
      const title = String(body.title ?? "").toLowerCase();
      const detail = String(body.detail ?? "").toLowerCase();
      const type = String(body.type ?? "").toLowerCase();
      const code = String(body.code ?? body.errorCode ?? body.error ?? "").toLowerCase();

      payloadText = [title, detail, type, code].join(" ");
    } catch {
      try {
        payloadText = (await responseClone.text()).toLowerCase();
      } catch {
        payloadText = "";
      }
    }

    if (!payloadText) {
      return false;
    }

    if (permissionDeniedMarkers.some(signal => payloadText.includes(signal))) {
      return false;
    }

    return sessionExpiredMarkers.some(signal => payloadText.includes(signal));
  }

  private async acquireTokenWithPopupQueue(tokenRefreshManager: TokenRefreshManager): Promise<string | null> {
    if (TokenRefreshMiddleware.popupReauthenticationPromise) {
      return await TokenRefreshMiddleware.popupReauthenticationPromise;
    }

    TokenRefreshMiddleware.popupReauthenticationPromise = (async () => {
      try {
        console.debug("TokenRefreshMiddleware: Attempting popup reauthentication");
        return await tokenRefreshManager.acquireTokenInteractive();
      } finally {
        TokenRefreshMiddleware.popupReauthenticationPromise = null;
      }
    })();

    return await TokenRefreshMiddleware.popupReauthenticationPromise;
  }

  private hasRetryMarker(context: ResponseContext): boolean {
    const headers = new Headers(context.init?.headers);
    return headers.get(RETRY_MARKER_HEADER) === RETRY_MARKER_VALUE;
  }

  private async retryRequest(context: ResponseContext, newAccessToken: string): Promise<Response> {
    const headers = new Headers(context.init?.headers);
    headers.set("Authorization", `Bearer ${newAccessToken}`);
    headers.set(RETRY_MARKER_HEADER, RETRY_MARKER_VALUE);

    const retryInit: RequestInit = {
      ...context.init,
      headers,
    };

    try {
      console.debug("TokenRefreshMiddleware: Retrying request with new token");
      return await fetch(context.url, retryInit);
    } catch (error) {
      console.error("TokenRefreshMiddleware: Retry request failed", error);
      return context.response;
    }
  }
}

const sessionExpiredMarkers = [
  "session expired",
  "expired session",
  "token expired",
  "invalid token",
  "invalid_token",
  "login required",
  "reauth",
  "unauthenticated",
  "authentication required",
  "interaction required",
  "consent required",
  "token_expired",
  "session_expired",
];

const permissionDeniedMarkers = [
  "insufficient permission",
  "insufficient permissions",
  "insufficient privileges",
  "permission",
  "forbidden",
  "access denied",
  "not authorized",
];
